import { useState, useMemo } from "react";
import ReactMarkdown from "react-markdown";
import Box from "@mui/material/Box";
import Popover from "@mui/material/Popover";
import Typography from "@mui/material/Typography";
import SvgIcon from "@mui/material/SvgIcon";
import { keyframes } from "@mui/system";

const blink = keyframes`
  0%, 100% { opacity: 1; }
  50%      { opacity: 0; }
`;

/* Custom Article Icon - Same as SourcesDialog */
const ArticleIcon = (props) => (
    <SvgIcon {...props} viewBox="0 0 18 18">
        <path
            d="M15.75 6V15.7448C15.7507 15.8432 15.732 15.9409 15.6949 16.0322C15.6579 16.1234 15.6032 16.2065 15.534 16.2766C15.4649 16.3468 15.3826 16.4026 15.2919 16.4409C15.2011 16.4792 15.1037 16.4993 15.0052 16.5H2.99475C2.79736 16.5 2.60804 16.4216 2.4684 16.2821C2.32875 16.1426 2.2502 15.9534 2.25 15.756V2.244C2.25 1.84125 2.58675 1.5 3.0015 1.5H11.2477L15.75 6ZM14.25 6.75H10.5V3H3.75V15H14.25V6.75ZM6 5.25H8.25V6.75H6V5.25ZM6 8.25H12V9.75H6V8.25ZM6 11.25H12V12.75H6V11.25Z"
            fill="#FB923C"
        />
    </SvgIcon>
);

/**
 * Inline citation badge — Figma node 206-8999 / 139-5300.
 * Interactive hover state shows source details.
 */
function CitationBadge({ number, source }) {
    const [anchorEl, setAnchorEl] = useState(null);

    const handleMouseEnter = (event) => {
        setAnchorEl(event.currentTarget);
    };

    const handleMouseLeave = () => {
        setAnchorEl(null);
    };

    const open = Boolean(anchorEl);

    return (
        <>
            <Box
                component="span"
                onMouseEnter={handleMouseEnter}
                onMouseLeave={handleMouseLeave}
                sx={{
                    display: "inline-flex",
                    alignItems: "center",
                    justifyContent: "center",
                    width: 22,
                    height: 22,
                    borderRadius: "999px",
                    backgroundColor: "#fffaf0",
                    color: "#3d3a37",
                    fontSize: 12,
                    fontWeight: 500,
                    fontFamily: "'Inter', sans-serif",
                    lineHeight: "18px",
                    verticalAlign: "middle",
                    mx: "2px",
                    cursor: "pointer",
                    border: "1px solid transparent", // Prevent layout shift if border added later
                    transition: "all 0.2s ease",
                    "&:hover": {
                        backgroundColor: "#FFF7ED", // Slightly darker on hover?
                        color: "#96380d", // Dark orange text on hover
                    },
                }}
            >
                {number}
            </Box>
            <Popover
                sx={{
                    pointerEvents: "none", // Allow hover through? Or tooltip behavior?
                }}
                open={open}
                anchorEl={anchorEl}
                anchorOrigin={{
                    vertical: "top",
                    horizontal: "center",
                }}
                transformOrigin={{
                    vertical: "bottom",
                    horizontal: "center",
                }}
                onClose={handleMouseLeave} // Close when mouse leaves trigger? (Popover blocks mouse leave if modal)
                disableRestoreFocus
                slotProps={{
                    paper: {
                        onMouseEnter: handleMouseEnter, // Keep open if hovering tooltip
                        onMouseLeave: handleMouseLeave,
                        sx: {
                            pointerEvents: "auto",
                            borderRadius: "6px",
                            boxShadow: "0px 0px 2px 0px rgba(0,0,0,0.1), 0px 2px 4px 0px rgba(0,0,0,0.1)",
                            backgroundColor: "#fbfaf9",
                            p: "4px",
                            mt: "-4px", // Slight offset upwards
                        },
                    },
                }}
            >
                {source && (
                    <Box
                        sx={{
                            display: "flex",
                            gap: "8px",
                            alignItems: "center",
                            p: "8px",
                            borderRadius: "6px",
                            maxWidth: 300,
                        }}
                    >
                        <ArticleIcon sx={{ fontSize: 18, flexShrink: 0 }} />
                        <Typography
                            sx={{
                                fontFamily: "'Inter', sans-serif",
                                fontSize: 14,
                                fontWeight: 400,
                                lineHeight: "20px",
                                color: "#3d3a37",
                            }}
                        >
                            {source.title ?? source.name}
                        </Typography>
                    </Box>
                )}
            </Popover>
        </>
    );
}

/**
 * Processes children from react-markdown and replaces [1], [2] etc.
 * with citation badges.
 */
function processCitations(children, sources) {
    if (!children) return children;
    return children.map((child, i) => {
        if (typeof child !== "string") return child;
        // Split on citation patterns like [1], [2]
        const parts = child.split(/(\[\d+\])/g);
        if (parts.length === 1) return child;
        return parts.map((part, j) => {
            const match = part.match(/^\[(\d+)\]$/);
            if (match) {
                const num = parseInt(match[1], 10);
                const source = sources && sources[num - 1]; // 1-based index
                return <CitationBadge key={`${i}-${j}`} number={match[1]} source={source} />;
            }
            return part;
        });
    });
}

/**
 * Returns ReactMarkdown components that render with citation processing.
 * Defined at module level; sources are passed in so the parent can pass data as props.
 */
function getMarkdownComponents(sources) {
    const withCitations = (children) =>
        processCitations(Array.isArray(children) ? children : [children], sources);
    return {
        p: ({ children, ...props }) => <p {...props}>{withCitations(children)}</p>,
        li: ({ children, ...props }) => <li {...props}>{withCitations(children)}</li>,
        h1: ({ children, ...props }) => (
            <div {...props} className="md-heading">{withCitations(children)}</div>
        ),
        h2: ({ children, ...props }) => (
            <div {...props} className="md-heading">{withCitations(children)}</div>
        ),
        h3: ({ children, ...props }) => (
            <div {...props} className="md-heading md-heading-sm">{withCitations(children)}</div>
        ),
        h4: ({ children, ...props }) => (
            <div {...props} className="md-heading md-heading-sm">{withCitations(children)}</div>
        ),
        h5: ({ children, ...props }) => (
            <div {...props} className="md-heading md-heading-sm">{withCitations(children)}</div>
        ),
        h6: ({ children, ...props }) => (
            <div {...props} className="md-heading md-heading-sm">{withCitations(children)}</div>
        ),
    };
}

/**
 * Renders markdown answer text with citation badges. showCursor shows a blinking cursor (e.g. while loading).
 */
export default function AnswerText({ text, showCursor, sources }) {
    const components = useMemo(() => getMarkdownComponents(sources), [sources]);

    return (
        <Box
            sx={{
                fontFamily: "'Inter', sans-serif",
                fontSize: 16,
                lineHeight: "24px",
                color: "#3d3a37",
                "& p": { margin: "0 0 12px" },
                "& p:last-child": { marginBottom: 0 },
                "& strong": { fontWeight: 600, color: "#3d3a37" },
                "& em": { fontStyle: "italic" },
                /* Headings — all consistent size, no browser defaults */
                "& .md-heading": {
                    fontFamily: "'Inter', sans-serif",
                    fontSize: 16,
                    lineHeight: "24px",
                    fontWeight: 600,
                    color: "#3d3a37",
                    margin: "16px 0 8px",
                    "&:first-child": { marginTop: 0 },
                },
                "& .md-heading-sm": {
                    fontWeight: 500,
                },
                /* Lists */
                "& ul, & ol": { paddingLeft: "24px", margin: "8px 0 12px" },
                "& li": { marginBottom: "6px", fontSize: 16, lineHeight: "24px" },
                "& li:last-child": { marginBottom: 0 },
                /* Blockquote */
                "& blockquote": {
                    margin: "16px 0",
                    padding: "12px 16px",
                    background: "#eff6ff",
                    borderLeft: "3px solid #3b82f6",
                    borderRadius: "0 8px 8px 0",
                    color: "#1d4ed8",
                    "& p": { margin: 0 },
                },
                /* Inline code */
                "& code": {
                    fontFamily: "ui-monospace, monospace",
                    fontSize: 13,
                    background: "rgba(0,0,0,0.05)",
                    padding: "2px 6px",
                    borderRadius: 4,
                },
                /* Code blocks */
                "& pre": {
                    margin: "12px 0",
                    padding: "12px 16px",
                    background: "#f7f7f7",
                    borderRadius: "8px",
                    overflow: "auto",
                    "& code": {
                        background: "none",
                        padding: 0,
                        fontSize: 13,
                        lineHeight: "20px",
                    },
                },
                /* Links */
                "& a": {
                    color: "#2563eb",
                    textDecoration: "underline",
                    textDecorationColor: "rgba(37, 99, 235, 0.3)",
                    "&:hover": {
                        textDecorationColor: "#2563eb",
                    },
                },
                /* Horizontal rule */
                "& hr": {
                    border: "none",
                    borderTop: "1px solid rgba(0,0,0,0.08)",
                    margin: "16px 0",
                },
                /* Tables */
                "& table": {
                    width: "100%",
                    borderCollapse: "collapse",
                    margin: "12px 0",
                    fontSize: 14,
                    lineHeight: "20px",
                },
                "& th, & td": {
                    border: "1px solid #e5e7eb",
                    padding: "8px 12px",
                    textAlign: "left",
                },
                "& th": {
                    fontWeight: 600,
                    background: "#f7f7f7",
                },
            }}
        >
            <ReactMarkdown components={components}>{text}</ReactMarkdown>

            {showCursor && (
                <Box
                    component="span"
                    sx={{
                        display: "inline-block",
                        width: 2,
                        height: 18,
                        backgroundColor: "#FF9E2C",
                        borderRadius: 1,
                        verticalAlign: "text-bottom",
                        ml: "2px",
                        animation: `${blink} 0.8s step-end infinite`,
                    }}
                />
            )}
        </Box>
    );
}
